import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  doc,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth'

export type ResourceSubmissionType = 'guide' | 'program' | 'provider' | 'job' | 'correction'
export type ResourceSubmissionStatus = 'pending' | 'approved' | 'rejected'

export interface ResourceSubmission {
  id: string
  type: ResourceSubmissionType
  title: string
  description: string
  link?: string
  category?: string
  tags?: string[]
  submitterUid: string
  submitterName: string
  submitterEmail: string
  submitterRole: string
  status: ResourceSubmissionStatus
  createdAt?: Timestamp
  reviewedAt?: Timestamp
  reviewedBy?: string
  reviewNote?: string
}

export interface NewResourceSubmission {
  type: ResourceSubmissionType
  title: string
  description: string
  link?: string
  category?: string
  tags?: string[]
}

/** Logs and rethrows so callers keep their own error handling (R7/R11). */
async function logged<T>(context: string, op: () => Promise<T>): Promise<T> {
  try {
    return await op()
  } catch (err: unknown) {
    console.warn(`[resourceSubmissions.${context}] write failed:`, err)
    throw err
  }
}

export async function submitResourceSuggestion(input: NewResourceSubmission): Promise<string> {
  const auth = useAuthStore.getState()
  const uid = auth.uid()
  const user = auth.user
  if (!uid || !user) throw new Error('Sign in required')

  const docRef = await logged('submitResourceSuggestion', () => addDoc(collection(db, 'resource_submissions'), {
    type: input.type,
    title: input.title.trim(),
    description: input.description.trim(),
    link: input.link?.trim() || '',
    category: input.category?.trim() || '',
    tags: input.tags ?? [],
    submitterUid: uid,
    submitterName: user.displayName || auth.userDoc?.displayName || 'Unknown',
    submitterEmail: user.email || auth.userDoc?.email || '',
    submitterRole: auth.primaryRole(),
    status: 'pending',
    createdAt: serverTimestamp(),
  }))

  return docRef.id
}

export async function fetchResourceSubmissions(): Promise<ResourceSubmission[]> {
  const snap = await getDocs(query(collection(db, 'resource_submissions'), orderBy('createdAt', 'desc')))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as ResourceSubmission))
}

export async function fetchApprovedSubmissions(): Promise<ResourceSubmission[]> {
  try {
    const snap = await getDocs(query(collection(db, 'resource_submissions'), where('status', '==', 'approved')))
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as ResourceSubmission))
      .sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0))
  } catch (err) {
    console.warn('[fetchApprovedSubmissions] query failed:', err)
    return []
  }
}

export async function deleteResourceSubmission(id: string): Promise<void> {
  await logged('deleteResourceSubmission', () => deleteDoc(doc(db, 'resource_submissions', id)))
}

export async function updateResourceSubmission(
  id: string,
  updates: Pick<NewResourceSubmission, 'title' | 'description' | 'link' | 'category'> & { tags?: string[]; reviewNote?: string },
): Promise<void> {
  await logged('updateResourceSubmission', () => updateDoc(doc(db, 'resource_submissions', id), {
    title: updates.title.trim(),
    description: updates.description.trim(),
    link: updates.link?.trim() || '',
    category: updates.category?.trim() || '',
    ...(updates.tags !== undefined ? { tags: updates.tags } : {}),
    ...(updates.reviewNote !== undefined ? { reviewNote: updates.reviewNote.trim() } : {}),
  }))
}

export async function reviewResourceSubmission(
  id: string,
  status: Extract<ResourceSubmissionStatus, 'approved' | 'rejected'>,
  reviewNote = '',
): Promise<void> {
  const uid = useAuthStore.getState().uid()
  await logged('reviewResourceSubmission', () => updateDoc(doc(db, 'resource_submissions', id), {
    status,
    reviewNote: reviewNote.trim(),
    reviewedAt: serverTimestamp(),
    reviewedBy: uid,
  }))
}
